import { useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import PilotageOverview from "./PilotageOverview";
import ExpensesTab, { ExpenseModal } from "./ExpensesTab";
import UrssafTab from "./UrssafTab";
import AnnualChargesTab from "./AnnualChargesTab";
import ForecastTab from "./ForecastTab";
import BilanTab from "./BilanTab";
import AlertsBanner from "./AlertsBanner";

const SUBTABS = [
  ["overview", "Vue d'ensemble"], ["pro", "Dépenses professionnelles"],
  ["perso", "Dépenses personnelles"], ["charges", "Charges annuelles"],
  ["urssaf", "Cotisations et impôts"], ["previsions", "Prévisions et objectifs"],
  ["bilan", "Bilan annuel"],
];
const MODES = [["mois", "Mois"], ["trimestre", "Trimestre"], ["annee", "Année"], ["custom", "Personnalisée"]];

export function periodRange(mode, month, custom) {
  const [y, m] = month.split("-").map(Number);
  if (mode === "mois") {
    const last = new Date(y, m, 0).getDate();
    return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, "0")}` };
  }
  if (mode === "trimestre") {
    const q0 = Math.floor((m - 1) / 3) * 3 + 1;
    const last = new Date(y, q0 + 3, 0).getDate();
    return { from: `${y}-${String(q0).padStart(2, "0")}-01`, to: `${y}-${String(q0 + 2).padStart(2, "0")}-${last}` };
  }
  if (mode === "annee") return { from: `${y}-01-01`, to: `${y}-12-31` };
  return { from: custom.from || `${month}-01`, to: custom.to || month + "-28" };
}

export default function PilotageGlobal() {
  const [sub, setSub] = useState("overview");
  const [mode, setMode] = useState("mois");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [quickAdd, setQuickAdd] = useState(null); // "pro" | "perso" | null
  const { from, to } = periodRange(mode, month, custom);
  const shift = (d) => {
    const [y, m] = month.split("-").map(Number);
    const nm = m + d;
    setMonth(`${nm > 12 ? y + 1 : nm < 1 ? y - 1 : y}-${String(nm > 12 ? 1 : nm < 1 ? 12 : nm).padStart(2, "0")}`);
  };
  const monthLabel = new Date(`${month}-15`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  return (
    <div className="flex-1 min-h-0 overflow-y-auto" data-testid="pilotage-global">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0B132B]">Pilotage global</h2>
          <p className="text-xs text-neutral-400 mt-0.5">Vue d'ensemble de votre situation financière — données réelles uniquement.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => shift(-1)} className="p-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50" data-testid="pilotage-prev"><ChevronLeft className="w-4 h-4" /></button>
          <span className="flex items-center gap-1.5 text-sm font-medium border border-neutral-200 rounded-lg px-3 py-1.5 bg-white capitalize" data-testid="pilotage-month-label">
            <CalendarDays className="w-3.5 h-3.5 text-neutral-400" />{mode === "annee" ? month.slice(0, 4) : monthLabel}
          </span>
          <button onClick={() => shift(1)} className="p-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50" data-testid="pilotage-next"><ChevronRight className="w-4 h-4" /></button>
          <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
            {MODES.map(([v, l]) => (
              <button key={v} onClick={() => setMode(v)} data-testid={`pilotage-mode-${v}`}
                className={`px-3 py-1.5 text-xs transition-colors ${mode === v ? "bg-[#6D5DF6] text-white" : "bg-white text-neutral-500 hover:bg-neutral-50"}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => setQuickAdd("pro")} data-testid="pilotage-quick-add"
            className="flex items-center gap-1.5 bg-[#6D5DF6] hover:bg-[#5B4BD6] text-white text-xs font-medium rounded-lg px-3.5 py-2 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Ajouter une dépense
          </button>
        </div>
      </div>
      {mode === "custom" && (
        <div className="flex items-center gap-2 mb-4">
          <input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="pilotage-custom-from" className="border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs" />
          <span className="text-xs text-neutral-400">au</span>
          <input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="pilotage-custom-to" className="border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs" />
        </div>
      )}
      <AlertsBanner onNavigate={setSub} />
      <div className="flex items-center gap-5 border-b border-neutral-100 mb-5 overflow-x-auto" data-testid="pilotage-subtabs">
        {SUBTABS.map(([v, l]) => (
          <button key={v} onClick={() => setSub(v)} data-testid={`pilotage-subtab-${v}`}
            className={`pb-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${sub === v ? "border-[#6D5DF6] text-[#6D5DF6] font-medium" : "border-transparent text-neutral-400 hover:text-neutral-600"}`}>{l}</button>
        ))}
      </div>
      {sub === "overview" && <PilotageOverview from={from} to={to} />}
      {sub === "pro" && <ExpensesTab type="pro" from={from} to={to} />}
      {sub === "perso" && <ExpensesTab type="perso" from={from} to={to} />}
      {sub === "charges" && <AnnualChargesTab />}
      {sub === "urssaf" && <UrssafTab from={from} to={to} />}
      {sub === "previsions" && <ForecastTab year={month.slice(0, 4)} />}
      {sub === "bilan" && <BilanTab year={month.slice(0, 4)} />}
      {quickAdd && <ExpenseModal type={quickAdd} onClose={() => setQuickAdd(null)} onSaved={() => setQuickAdd(null)}
        switchType={setQuickAdd} />}
    </div>
  );
}
